<template>
  <div :class="classList" :data-id="_uid">
    <div class="a-message__icon">
      <slot name="icon"></slot>
    </div>
    <div class="a-message__text">
      <slot name="text"></slot>
    </div>
    <div class="a-message__close" v-if="affixed" @click="closeButton">
      <XMarkIcon />
    </div>
  </div>
</template>

<script>
import { XMarkIcon } from '@/components/icons/fontIcons';

export default {
  name: 'VMessage',
  components: {
    XMarkIcon,
  },
  props: {
    type: {
      type: String,
      required: true,
      note: 'Selects the colour styling ("warning", "error", etc.)',
    },
    subtype: {
      type: String,
      note: 'Selects the appearance between "outlined" or "basic".',
    },
    fullwidth: {
      type: Boolean,
      note: 'Toggles width 100%.',
    },
    affixed: {
      type: Boolean,
      note: 'If true messages display in bottom left in a fixed position.',
    },
    closeButton: {
      required: false,
      // Should probably only be type function
      type: [Object, Function],
      default: () => {},
      note: 'A function to be called when the message is closed.',
    },
  },
  computed: {
    classList() {
      const defaultClass = 'a-message is-raleway';
      const type = ` a-message--${this.type}`;
      const subtype = this.subtype ? ` a-message--${this.subtype}` : '';
      const fullwidth = this.fullwidth ? ' a-message--fullwidth' : '';
      const affixed = this.affixed ? ' a-message--affixed' : '';
      return defaultClass + type + subtype + fullwidth + affixed;
    },
  },
};
</script>

<style scoped>
.a-message {
  --icon-width: 52px;
  --icon-width-outlined: 42px;

  padding: var(--spacing-base);
  border-radius: 3px;
  color: #fff;
  position: relative;
  width: auto;
  display: inline-block;
  font-weight: 600;
}

.a-message:after {
  content: '';
  display: block;
  clear: both;
}

.a-message svg {
  height: 14px;
  width: 14px;
  position: absolute;
  transform: translate3d(-50%, -50%, 0);
  top: 50%;
  left: 50%;
}

.a-message__icon {
  float: left;
  padding: calc(var(--spacing-base) + 1px);
  margin: calc(0px - var(--spacing-base));
  line-height: 0;
  width: var(--icon-width);
  position: absolute;
  transform: translate3d(0, -50%, 0);
  top: 50%;
  top: calc(50% + var(--spacing-base));
  height: 100%;
}

.a-message__text {
  float: left;
  margin-left: 36px;
}

.a-message__close {
  position: absolute;
  right: -8px;
  padding: 8px;
  top: -8px;
  border-radius: 30px;
  background: var(--color-error);
  border: 1px solid var(--color-error-dark);
  cursor: pointer;
}

.a-message__close svg {
  padding: 2px;
}

.a-message--fullwidth {
  width: 100%;
}

.a-message--affixed {
  box-shadow: 0px 10px 3px -5px rgba(0, 0, 0, 0.15);
  z-index: 5000;
  top: auto;
  bottom: var(--spacing-base);
  left: var(--spacing-base);
  position: fixed;
}

.a-message--info {
  background: var(--color-info);
  border-color: var(--color-info-dark);
}

.a-message--success {
  background: var(--color-success);
  border-color: var(--color-success-dark);
}

.a-message--warning {
  background: var(--color-warning);
  border-color: var(--color-warning-dark);
}

.a-message--error {
  background: var(--color-error);
  border-color: var(--color-error-dark);
}

.a-message--outlined {
  border-width: 1px;
  border-style: solid;
  padding: var(--spacing-small);
  background: #fff;
  border-radius: 3px;
}

.a-message--outlined .a-message__icon {
  width: var(--icon-width-outlined);
  margin: calc(0px - var(--spacing-small));
  top: calc(50% + var(--spacing-small));
  border-radius: 0;
  background: transparent;
}

.a-message--outlined .a-message__icon svg {
  width: 12px;
  height: 12px;
}

.a-message--outlined .a-message__text {
  margin-left: calc(var(--icon-width-outlined) - var(--spacing-small));
}

.a-message--outlined.a-message--info .a-message__text,
.a-message--outlined.a-message--info .a-message__icon {
  color: var(--color-info);
}

.a-message--outlined.a-message--success .a-message__text,
.a-message--outlined.a-message--success .a-message__icon {
  color: var(--color-success);
}

.a-message--outlined.a-message--warning .a-message__text,
.a-message--outlined.a-message--warning .a-message__icon {
  color: var(--color-warning);
}

.a-message--outlined.a-message--error .a-message__text,
.a-message--outlined.a-message--error .a-message__icon {
  color: var(--color-error);
}

.a-message--basic {
  padding: 0;
  background: transparent;
  border: 0;
  border-radius: 0;
  font-size: var(--font-size-small);
}

.a-message--basic .a-message__icon {
  background: transparent;
}

.a-message--basic .a-message__icon svg {
  height: 16px;
  width: 16px;
}

.a-message--basic .a-message__text {
  margin-left: 0;
  padding-left: var(--spacing-large);
}

.a-message--basic.a-message--info .a-message__icon,
.a-message--basic.a-message--info .a-message__text {
  color: var(--color-info);
}

.a-message--basic.a-message--success .a-message__icon,
.a-message--basic.a-message--success .a-message__text {
  color: var(--color-success);
}

.a-message--basic.a-message--warning .a-message__icon,
.a-message--basic.a-message--warning .a-message__text {
  color: var(--color-warning);
}

.a-message--basic.a-message--error .a-message__icon,
.a-message--basic.a-message--error .a-message__text {
  color: var(--color-error);
}
</style>
